import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import { pipelinesApi } from "../api/pipelines";
import { JarvisComposer } from "../components/JarvisComposer";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { QueryBoundary } from "../components/QueryBoundary";
import { DragBanner } from "../components/board/DragBanner";
import { DroppableColumn } from "../components/board/DroppableColumn";
import { EnrollModal } from "../components/board/EnrollModal";
import { SortableCard } from "../components/board/BoardCard";
import { PipelineCardBody } from "../components/board/PipelineCardBody";
import {
  columnLabel,
  columnOf,
  parseCardId,
  parseColumnId,
  type BoardColumn,
  type ColumnKey,
} from "../components/board/ids";
import { useToast } from "../components/toast/context";
import { useLiveUpdates } from "../lib/hooks/useLiveUpdates";
import {
  isStale,
  sortByPrio,
  sortByStale,
  type BoardSortMode,
} from "../lib/pipelineBoard";
import type { PipelineBoard, PipelineBoardCard } from "../types";

const SORT_MODE_KEY = "pipeline-board-sort";
const SORT_MODES: { value: BoardSortMode; label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "prio", label: "Priority" },
  { value: "stale", label: "Stale" },
];

function loadSortMode(): BoardSortMode {
  if (typeof window === "undefined") return "manual";
  const raw = window.localStorage.getItem(SORT_MODE_KEY);
  return raw === "prio" || raw === "stale" ? raw : "manual";
}

// Pure view projection over a column's cached cards — never mutates the cache.
function projectColumn(
  cards: PipelineBoardCard[],
  mode: BoardSortMode,
): PipelineBoardCard[] {
  if (mode === "prio") return sortByPrio(cards);
  if (mode === "stale") return sortByStale(cards);
  return cards;
}

export function PipelineBoardPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [activeCard, setActiveCard] = useState<PipelineBoardCard | null>(null);
  const [overColumn, setOverColumn] = useState<ColumnKey | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [sortMode, setSortMode] = useState<BoardSortMode>(loadSortMode);
  // Parked members (not being worked) stay off the board unless asked for.
  const [searchParams, setSearchParams] = useSearchParams();
  const showParked = searchParams.get("parked") === "1";
  const boardKey = ["pipeline-board", id, showParked] as const;
  const toggleParked = () => {
    const next = new URLSearchParams(searchParams);
    if (showParked) next.delete("parked");
    else next.set("parked", "1");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SORT_MODE_KEY, sortMode);
    }
  }, [sortMode]);

  const query = useQuery({
    queryKey: boardKey,
    queryFn: () => pipelinesApi.board(id!, showParked),
    enabled: !!id,
  });

  // Live updates fire on pipeline-definition changes. Membership writes
  // live on contact rows and don't emit pipeline topics yet — mutations
  // below refresh via the returned board snapshot instead.
  const liveTopics = useMemo(() => (id ? [`pipelines:${id}`] : []), [id]);
  useLiveUpdates(liveTopics, (ev) => {
    if (ev.table === "pipelines" && ev.op === "DELETE") {
      toast.show("This pipeline was deleted");
      navigate("/pipelines", { replace: true });
      return;
    }
    qc.invalidateQueries({ queryKey: ["pipeline-board", id] });
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  function findCardInSnapshot(
    snap: PipelineBoard | undefined,
    column: ColumnKey,
    contactId: string,
  ): PipelineBoardCard | null {
    if (!snap) return null;
    return snap.columns[column]?.find((c) => c.contact_id === contactId) || null;
  }

  function resolveColumnIdFromOver(overId: string): ColumnKey | null {
    const col = parseColumnId(overId);
    if (col) return col;
    const card = parseCardId(overId);
    return card?.column || null;
  }

  function handleDragStart(ev: DragStartEvent) {
    const parsed = parseCardId(String(ev.active.id));
    if (!parsed) return;
    setActiveCard(findCardInSnapshot(query.data, parsed.column, parsed.contactId));
  }

  async function handleDragEnd(ev: DragEndEvent) {
    setActiveCard(null);
    setOverColumn(null);
    const active = parseCardId(String(ev.active.id));
    if (!active || !ev.over) return;

    const overRaw = String(ev.over.id);
    const targetColumn = resolveColumnIdFromOver(overRaw);
    if (!targetColumn) return;

    const snap = query.data;
    if (!snap || !id) return;

    if (active.column === targetColumn) {
      // Within-column reorder only persists in manual mode — prio/stale are
      // pure view projections, so a drag there must not write sort_order.
      if (sortMode !== "manual") return;
      const colCards = snap.columns[active.column] || [];
      const fromIdx = colCards.findIndex((c) => c.contact_id === active.contactId);
      const overCard = parseCardId(overRaw);
      const toIdx = overCard
        ? colCards.findIndex((c) => c.contact_id === overCard.contactId)
        : colCards.length - 1;
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

      const reordered = arrayMove(colCards, fromIdx, toIdx);
      const nextSnap: PipelineBoard = {
        ...snap,
        columns: { ...snap.columns, [active.column]: reordered },
      };
      qc.setQueryData(boardKey, nextSnap);

      try {
        const fresh = await pipelinesApi.reorderBoardColumn(
          id,
          { stage_id: active.column, contact_ids: reordered.map((c) => c.contact_id) },
          showParked,
        );
        qc.setQueryData(boardKey, fresh);
      } catch (err) {
        toast.error(err);
        qc.invalidateQueries({ queryKey: ["pipeline-board", id] });
      }
      return;
    }

    // Cross-column move. _unstaged is a fallback view for orphaned stage
    // ids — memberships always have a stage, so it's not a drop target.
    if (targetColumn === "_unstaged") return;

    const source = [...(snap.columns[active.column] || [])];
    const target = [...(snap.columns[targetColumn] || [])];
    const idx = source.findIndex((c) => c.contact_id === active.contactId);
    if (idx === -1) return;
    const [moved] = source.splice(idx, 1);
    target.push(moved);
    qc.setQueryData(boardKey, {
      ...snap,
      columns: { ...snap.columns, [active.column]: source, [targetColumn]: target },
    });

    try {
      // A parked card dropped on a stage is reactivated there.
      const fresh = await pipelinesApi.moveBoardCard(
        id,
        { contact_id: active.contactId, target_stage_id: targetColumn },
        showParked,
      );
      qc.setQueryData(boardKey, fresh);
    } catch (err) {
      toast.error(err);
      qc.invalidateQueries({ queryKey: ["pipeline-board", id] });
    }
  }

  return (
    <div className="page">
      <PageHeader
        title={query.data?.pipeline.data.name || "Pipeline"}
        description="Columns are stages, cards are contacts. Drag a card to move it between stages."
        actions={
          <>
            <button
              type="button"
              className="btn btn-main btn-sm"
              disabled={!query.data || query.data.pipeline.data.is_default}
              onClick={() => setEnrollOpen(true)}
            >
              + Add contact
            </button>
            <Link to="/pipelines" className="btn btn-ghost btn-sm">
              ← All pipelines
            </Link>
          </>
        }
      />

      <QueryBoundary query={query}>
        {(board) => {
          const stageColumns: BoardColumn[] = board.stages.map((s) => ({
            key: s.id,
            label: `Stage ${String(s.position).padStart(2, "0")}`,
            title: s.name,
            description: s.description,
          }));
          const unstagedCards = board.columns["_unstaged"] || [];
          const allColumns: BoardColumn[] = [
            ...stageColumns,
            ...(unstagedCards.length
              ? [
                  {
                    key: "_unstaged",
                    label: "Unstaged",
                    title: "Unstaged",
                    subtitle: "Stage id no longer exists on this pipeline",
                  },
                ]
              : []),
          ];
          const pipelineData = board.pipeline.data;

          return (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 mb-5">
                <div className="panel panel--flush">
                  <div className="flex flex-wrap items-center gap-4 px-6 py-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-semibold text-ink">
                          {pipelineData.name}
                        </span>
                        <Pill value={pipelineData.status} />
                        {pipelineData.is_default ? (
                          <span className="pill pill--muted">catch-all</span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-xs text-ink-low truncate">
                        {pipelineData.slug}
                        {pipelineData.goal ? ` · ${pipelineData.goal}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={clsx("btn btn-sm shrink-0", showParked ? "btn-main" : "btn-ghost")}
                      aria-pressed={showParked}
                      title="Parked contacts are not being worked: same stage, off the board"
                      onClick={toggleParked}
                    >
                      Show parked
                      <span className="tnum">{pipelineData.stats?.parked ?? 0}</span>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      {SORT_MODES.map((m) => (
                        <button
                          key={m.value}
                          type="button"
                          className={clsx(
                            "btn btn-sm",
                            sortMode === m.value ? "btn-main" : "btn-ghost",
                          )}
                          onClick={() => setSortMode(m.value)}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    <div className="text-right text-sm text-ink-low shrink-0 space-y-0.5">
                      <div>
                        <span className="tnum font-medium text-ink">
                          {pipelineData.stats?.active ?? 0}
                        </span>{" "}
                        active
                      </div>
                      <div>
                        <span className="tnum font-medium text-ink">
                          {pipelineData.stats?.parked ?? 0}
                        </span>{" "}
                        parked
                      </div>
                      <div>
                        <span className="tnum font-medium text-ink">
                          {pipelineData.stats?.goal_reached ?? 0}
                        </span>{" "}
                        goal reached
                      </div>
                    </div>
                  </div>
                </div>
                <JarvisComposer
                  refObj={{ type: "pipeline", id: pipelineData.slug }}
                />
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={(ev) => {
                  if (!ev.over) {
                    setOverColumn(null);
                    return;
                  }
                  setOverColumn(resolveColumnIdFromOver(String(ev.over.id)));
                }}
                onDragCancel={() => {
                  setActiveCard(null);
                  setOverColumn(null);
                }}
              >
                {activeCard ? (
                  <DragBanner
                    name={activeCard.name || activeCard.contact_id}
                    fromLabel={columnLabel(
                      allColumns,
                      columnOf(query.data, activeCard),
                    )}
                    toLabel={columnLabel(allColumns, overColumn)}
                  />
                ) : null}

                <div className="board-scroll">
                  <div className="board-strip">
                    {allColumns.map((col) => (
                      <DroppableColumn
                        key={col.key}
                        column={col}
                        cards={projectColumn(
                          board.columns[col.key] || [],
                          sortMode,
                        )}
                        isOver={overColumn === col.key}
                        renderCard={(card) => (
                          <SortableCard
                            key={card.contact_id}
                            column={col.key}
                            contactId={card.contact_id}
                            className={clsx(
                              isStale(card) && "kcard--stale",
                              card.status === "parked" && "kcard--parked",
                            )}
                          >
                            <PipelineCardBody card={card} />
                          </SortableCard>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <DragOverlay dropAnimation={null}>
                  {activeCard ? (
                    <div className="kcard-dragging">
                      <PipelineCardBody card={activeCard} />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          );
        }}
      </QueryBoundary>

      {enrollOpen && query.data ? (
        <EnrollModal
          alreadyEnrolled={
            new Set(
              Object.values(query.data.columns)
                .flat()
                .map((c) => c.contact_id),
            )
          }
          onClose={() => setEnrollOpen(false)}
          onEnroll={async (contact) => {
            // A parked member comes back in its own stage, not as a new card.
            await pipelinesApi.enroll(query.data!.pipeline.data.slug, contact.id);
            await qc.invalidateQueries({ queryKey: ["pipeline-board", id] });
          }}
        />
      ) : null}
    </div>
  );
}
